import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  getPetPhotos, uploadPetPhoto, updatePetPhoto,
  deletePetPhoto, thumbUrl, photoUrl
} from '../services/petMediaApi';

const MAX_MB = 10;

// ── Authenticated image with token header ─────────────────────
function AuthImg({ src, alt, className, style }) {
  const [objectUrl, setObjectUrl] = useState('');
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!src) return;
    const token = localStorage.getItem('token');
    fetch(src, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => {
        if (!r.ok) throw new Error('Not found');
        return r.blob();
      })
      .then(b => setObjectUrl(URL.createObjectURL(b)))
      .catch(() => setErr(true));
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  if (err) return <div className="pm-photo-placeholder">📷</div>;
  if (!objectUrl) return <div className="pm-photo-placeholder pm-photo-loading" />;
  return <img src={objectUrl} alt={alt} className={className} style={style} />;
}

// ── Single photo card ─────────────────────────────────────────
function PhotoCard({ photo, petName, onTogglePublic, onDelete, onEditCaption }) {
  const [editing, setEditing] = useState(false);
  const [caption, setCaption] = useState(photo.caption || '');

  const saveCaption = async () => {
    await onEditCaption(photo._id, caption);
    setEditing(false);
  };

  return (
    <div className="pm-photo-card">
      <div className="pm-photo-thumb-wrap">
        <AuthImg
          src={thumbUrl(photo.thumbnailFilename)}
          alt={caption || petName}
          className="pm-photo-thumb"
        />
        <div className="pm-photo-overlay">
          <a
            href={photoUrl(photo.filename)}
            target="_blank"
            rel="noreferrer"
            className="pm-photo-action"
            title="View full size"
          >⤢</a>
          <button
            className="pm-photo-action pm-photo-del"
            onClick={() => onDelete(photo._id)}
            title="Delete"
          >🗑</button>
        </div>
      </div>

      <div className="pm-photo-meta">
        {editing ? (
          <div className="pm-photo-caption-edit">
            <input
              className="pm-input"
              value={caption}
              onChange={e => setCaption(e.target.value)}
              maxLength={256}
              onKeyDown={e => { if (e.key === 'Enter') saveCaption(); if (e.key === 'Escape') setEditing(false); }}
              autoFocus
            />
            <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
              <button className="pm-btn pm-btn-primary pm-btn-sm" onClick={saveCaption}>Save</button>
              <button className="pm-btn pm-btn-ghost pm-btn-sm" onClick={() => setEditing(false)}>Cancel</button>
            </div>
          </div>
        ) : (
          <p
            className="pm-photo-caption"
            onClick={() => setEditing(true)}
            title="Click to edit caption"
          >
            {photo.caption || <span className="pm-photo-caption-empty">Add caption…</span>}
          </p>
        )}

        <div className="pm-photo-visibility">
          <span style={{ fontSize: 12, color: 'var(--text3, #718096)' }}>
            {photo.showOnPublicProfile ? '🌐 Public' : '🔒 Private'}
          </span>
          <button
            className={`pm-toggle ${photo.showOnPublicProfile ? 'on' : ''}`}
            onClick={() => onTogglePublic(photo._id, !photo.showOnPublicProfile)}
            title={photo.showOnPublicProfile ? 'Make private' : 'Show on public profile'}
          >
            <span className="pm-toggle-thumb" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Upload dropzone ───────────────────────────────────────────
function UploadZone({ petId, onUploaded }) {
  const inputRef    = useRef();
  const [drag, setDrag]     = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [file, setFile]     = useState(null);
  const [caption, setCaption] = useState('');
  const [isPublic, setIsPublic] = useState(false);

  const selectFile = f => {
    if (!f) return;
    if (!f.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (f.size > MAX_MB * 1024 * 1024) { toast.error(`Image must be under ${MAX_MB} MB`); return; }
    setFile(f);
    const reader = new FileReader();
    reader.onload = e => setPreview(e.target.result);
    reader.readAsDataURL(f);
  };

  const onDrop = e => {
    e.preventDefault();
    setDrag(false);
    selectFile(e.dataTransfer.files[0]);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      fd.append('caption', caption);
      fd.append('showOnPublicProfile', String(isPublic));
      const res = await uploadPetPhoto(petId, fd);
      toast.success('Photo uploaded');
      onUploaded(res.data.media);
      setFile(null);
      setPreview(null);
      setCaption('');
      setIsPublic(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally { setUploading(false); }
  };

  if (preview) {
    return (
      <div className="pm-upload-preview">
        <img src={preview} alt="Preview" className="pm-upload-preview-img" />
        <div className="pm-upload-preview-form">
          <div className="pm-field">
            <label className="pm-label">Caption (optional)</label>
            <input className="pm-input" value={caption} onChange={e => setCaption(e.target.value)} maxLength={256} placeholder="Describe this photo…" />
          </div>
          <div className="pm-toggle-row" style={{ paddingTop: 8 }}>
            <span style={{ fontSize: 13 }}>Show on public profile</span>
            <button type="button" className={`pm-toggle ${isPublic ? 'on' : ''}`} onClick={() => setIsPublic(v => !v)}>
              <span className="pm-toggle-thumb" />
            </button>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="pm-btn pm-btn-primary" onClick={handleUpload} disabled={uploading}>
              {uploading ? 'Uploading…' : '⬆ Upload'}
            </button>
            <button className="pm-btn pm-btn-ghost" onClick={() => { setFile(null); setPreview(null); }}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`pm-dropzone ${drag ? 'drag' : ''}`}
      onDragOver={e => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={onDrop}
      onClick={() => inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={e => selectFile(e.target.files[0])}
      />
      <div className="pm-dropzone-icon">📷</div>
      <p className="pm-dropzone-text">Drop a photo here, or click to browse</p>
      <p className="pm-dropzone-hint">JPEG, PNG, WebP · Max {MAX_MB} MB</p>
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────
export default function PetPhotoManager({ petId, petName }) {
  const [photos, setPhotos]   = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPetPhotos(petId);
      setPhotos(res.data.photos);
    } catch { toast.error('Failed to load photos'); }
    finally { setLoading(false); }
  }, [petId]);

  useEffect(() => { load(); }, [load]);

  const handleTogglePublic = async (mediaId, val) => {
    try {
      await updatePetPhoto(petId, mediaId, { showOnPublicProfile: val });
      setPhotos(ps => ps.map(p => p._id === mediaId ? { ...p, showOnPublicProfile: val } : p));
    } catch { toast.error('Failed to update'); }
  };

  const handleDelete = async mediaId => {
    if (!window.confirm('Delete this photo? This cannot be undone.')) return;
    try {
      await deletePetPhoto(petId, mediaId);
      setPhotos(ps => ps.filter(p => p._id !== mediaId));
      toast.success('Photo deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const handleEditCaption = async (mediaId, caption) => {
    try {
      await updatePetPhoto(petId, mediaId, { caption });
      setPhotos(ps => ps.map(p => p._id === mediaId ? { ...p, caption } : p));
    } catch { toast.error('Failed to save caption'); }
  };

  const handleUploaded = media => setPhotos(ps => [media, ...ps]);

  return (
    <div className="pm-section">
      <UploadZone petId={petId} onUploaded={handleUploaded} />

      {loading ? (
        <div className="pm-loading">Loading photos…</div>
      ) : photos.length === 0 ? (
        <div className="pm-empty">
          <span className="pm-empty-icon">📷</span>
          <p>No photos yet. Upload the first one above.</p>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--text3, #718096)' }}>
              {photos.length} photo{photos.length !== 1 ? 's' : ''} · {photos.filter(p => p.showOnPublicProfile).length} public
            </span>
          </div>
          <div className="pm-photo-grid">
            {photos.map(photo => (
              <PhotoCard
                key={photo._id}
                photo={photo}
                petName={petName}
                onTogglePublic={handleTogglePublic}
                onDelete={handleDelete}
                onEditCaption={handleEditCaption}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
